/**
 * Resale settlements: what a post-sale covenant owes when the work sells again.
 *
 * The screen reads `ondo.resale_settlements`, never a feed. `record()` and
 * `notify()` both swallow their failures, so a feed line can be silently
 * missing while the row exists — and a screen answering "what is owed" from a
 * feed would be answering "what happened" instead.
 */
export const SETTLEMENT_COPY = {
  loading: "Reading your covenant ledger…",
  errorTitle: "Can’t load your resale settlements",

  /**
   * The empty state, and it says what has to be TRUE rather than what has to be
   * built. Most galleries will sit here for years: a settlement needs a work
   * sold under a covenant AND resold inside its term AND that resale recorded
   * on ONDO, and the last of those is the one a reader will not guess.
   */
  empty: "No covenant has produced a settlement yet.",
  emptyDetail:
    "A settlement arises when a work you sold under a post-sale covenant is sold again inside its term, and that resale is recorded on ONDO. A resale that happens privately, off the registry, leaves no row here — there is nothing for a covenant to attach to.",

  /** 'covenant ledger · 3 settlements'. */
  ledger: (n: number) =>
    `covenant ledger · ${n} ${n === 1 ? "settlement" : "settlements"}`,

  /**
   * The banner. It corrects two things a reader is likely to assume from the
   * word "automatic": that ONDO takes the money, and that it catches every
   * resale. Neither is true and both matter.
   */
  lead: "A covenant’s terms were fixed at the original sale and cannot be varied.",
  leadDetail:
    "When a work is resold on ONDO inside its term, the agreed share of the profit falls due automatically — there is nothing to accept, decline or counter. ONDO records what is owed and who has paid it; it does not move money.",

  /** What a settlement is doing. Derived from six timestamps, never stored. */
  state: {
    due: "Settlement due",
    awaiting_rate: "Awaiting a rate",
    collected: "Collected",
    settled: "Settled",
    nothing_owed: "Nothing owed",
    /**
     * The resale was withdrawn. NOT "cancelled" and not "expired": somebody
     * unsaid a sale, and the settlement it caused stopped being owed as a
     * consequence rather than by anybody’s decision about the money.
     */
    void: "Resale withdrawn",
  },

  /** The group headings. */
  groups: {
    due: "Due now",
    closed: "Settled",
    nothingOwed: "Nothing owed",
    locked: "Live covenants that lock",
    lockedNote: "No resale is possible on these until the term ends, or you lift the lock.",
  },

  covenantTerms: "Post-sale covenant",
  fixedOn: "fixed",
  termTo: "term to",
  resoldOn: "resold",
  byReseller: "by",
  soldOn: "sold",
  toBuyer: "to",
  recordedBy: "Recorded by",
  dueLabel: "Due to this gallery",
  /** An em dash, for a figure that does not exist yet rather than one that is zero. */
  pending: "—",
  wouldOwe: (share: string) => `${share} of profit`,

  dueToGallery: (amount: string) => `${amount} to this gallery`,
  dueToArtist: (amount: string) => `${amount} to pass on`,
  awaitingCount: (n: number) =>
    n === 1 ? "one awaiting a rate" : `${n} awaiting a rate`,

  /** The arithmetic panel, in the order a reader checks it. */
  figures: {
    original: "Original sale",
    resale: "Resale price",
    converted: (from: string, to: string) => `Resale in ${to} (from ${from})`,
    profit: "Profit",
    share: (percent: string) => `Share (${percent})`,
    gallery: (percent: string) => `Gallery (${percent})`,
    artist: (percent: string) => `Artist (${percent})`,
  },

  /** The sentence under the panel. Every state answers, including the odd ones. */
  notes: {
    due: "Neither party may vary these terms. Record the collection once the money has been received.",
    awaitingRate: (from: string, to: string) =>
      `The resale was recorded in ${from} and this covenant is in ${to}. State the rate you are settling at — it is recorded against your name, shown to the artist, and cannot be changed afterwards.`,
    collected1: "Collected. The artist’s share has not been passed on yet.",
    collectedOffRegistry:
      "Collected. The artist’s share has not been passed on yet — they hold no ONDO id, so recording it here will not notify them.",
    collectedNoArtist: "Collected. This covenant owes the artist nothing, so there is nothing to pass on.",
    collected: "Collected",
    remitted: "remitted to",
    confirmed: "confirmed received by the artist",
    disputed: "DISPUTED by the artist",
    noProfit:
      "The work resold at or below its original price. The share is of the profit, so nothing is due — the row is kept as the record that the covenant was tested.",
    notElected:
      "The artist did not elect a share on this covenant, so their half was never owed and records zero rather than being skipped.",
    void: "The resale behind this settlement was withdrawn, so nothing is owed under it. The row is kept rather than deleted, because it is the record that the covenant was tested.",
  },

  /** The controls. Each says what the press actually does, and none moves money. */
  actions: {
    rate: "State the rate",
    collect: "Record collection",
    remit: "Record remittance",
    unlock: "Lift the lock",
  },

  /** The rate dialogue, which is the only control on this screen taking a number. */
  rate: {
    label: (from: string, to: string) => `${to} per 1 ${from}`,
    hint: "Recorded once, against your name, and never editable.",
    confirm: "State the rate and record collection",
    cancel: "Cancel",
    invalid: "A rate has to be a number greater than zero.",
  },

  locked: "Locked",
  lockedDetail:
    "Cannot be transferred on until the covenant term expires, or the gallery unlocks it. Lifting the lock is recorded with its date and its author, and does not change the share.",

  /**
   * Shown where a control would be, to a member below the admin rung.
   *
   * Says whose decision it is rather than that the reader lacks a permission.
   * Every act on this screen asserts that money moved or that a covenant was
   * released, and `ondo.settle_resale()`, `ondo.answer_settlement()` and
   * `ondo.unlock_covenant()` all require admin on the covenant-holding firm —
   * so this is not a feature being withheld, it is the same sentence the
   * database would say, said before the press instead of after it.
   */
  needsAdmin:
    "Recording a settlement commits the gallery, so it is a director’s to make. What is owed is shown here for everyone.",
} as const;
